import React, { useMemo } from "react";
import ModuleDetail from "../common/ModuleDetail";
import { COUNTRY_CODES } from "../../constants/COUNTRY_CODES";
import { COUNTRY_TIMEZONE } from "../../constants/COUNTRY_TIMEZONE"; 
import { Country, State, City } from "country-state-city";

// 1. Static Options
const countryOptions = Country.getAllCountries().map(c => ({
  value: c.name,
  label: c.name
}));

const uniqueCurrencies = Array.from(new Map(COUNTRY_TIMEZONE.map(item => [item.currency.code, item])).values());
const currencyCodeOptions = uniqueCurrencies.map((item) => ({
  value: item.currency.code, 
  label: `${item.currency.code} - ${item.currency.name}` 
}));

const uniqueSymbols = Array.from(new Map(COUNTRY_TIMEZONE.map(item => [item.currency.symbol, item])).values());
const currencySymbolOptions = uniqueSymbols.map((item) => ({
  value: item.currency.symbol, 
  label: `${item.currency.symbol} - ${item.currency.name}` 
}));

const phoneCodeOptions = COUNTRY_CODES.map(item => ({
  value: item.country_code,
  label: `${item.country} (${item.country_code})`
}));

// 2. 🌟 CACHE OBJECTS to stop React-Select from resetting your search text!
const stateOptionsCache = {};
const cityOptionsCache = {};

// 3. Searchable Helper
const handleSearchableChange = (arg1, arg2, arg3, arg4, fieldName, extraLogic = null) => {
  let val, data, setData;
  
  if (typeof arg4 === 'function') {
      val = arg2; data = arg3; setData = arg4;
  } else if (typeof arg3 === 'function') {
      val = arg1; data = arg2; setData = arg3;
  } else {
      return; 
  }

  const actualString = val && typeof val === 'object' ? val.value : val;
  let nextState = { ...data, [fieldName]: val || "" };
  
  if (extraLogic) {
      nextState = extraLogic(nextState, actualString);
  }

  setData(nextState);
};

const safeString = (val) => (val && typeof val === 'object' ? val.value : val);


const CompanyDetail = ({ permissions }) => { 

  const fields = useMemo(() => ({
    title: "name",
    subtitle: "systememail",
    status: "isactive",

    overview: [
      { key: "name", label: "Company Name", type: "text" },
      { key: "systememail", label: "System Email", type: "email" },
      { key: "adminemail", label: "Admin Email", type: "email" },
      { key: "userlicenses", label: "User Licenses", type: "number" },
      { 
        key: "currency_symbol", 
        label: "Currency Symbol", 
        type: "select", 
        isSearchable: true, 
        props: { isSearchable: true }, // Fallback for some custom wrappers
        options: currencySymbolOptions,
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "currency_symbol")
      },
      { 
        key: "currency", 
        label: "Currency Code", 
        type: "select", 
        isSearchable: true, 
        props: { isSearchable: true },
        options: currencyCodeOptions,
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "currency") 
      }, 
    ],

    details: [
      { key: "name", label: "Company Name", type: "text" },
      { key: "tenantcode", label: "Tenant Code", type: "text" },
      { key: "userlicenses", label: "User Licenses", type: "number" },
      { key: "isactive", label: "Active Status", type: "boolean" },
      { key: "systememail", label: "System Email", type: "email" },
      { key: "adminemail", label: "Admin Email", type: "email" },
      { key: "logourl", label: "Logo URL", type: "url" },
      { key: "sidebarbgurl", label: "Sidebar Background URL", type: "url" },
      { key: "sourceschema", label: "Source Schema", type: "text" },
      { key: "platform_name", label: "Platform Name", type: "text" },
      { key: "platform_api_endpoint", label: "Platform API Endpoint", type: "json" },
      { key: "is_external", label: "External Company", type: "boolean" },
      { key: "has_wallet", label: "Has Wallet", type: "boolean" },
      { key: "created_at", label: "Created At", type: "datetime" },
      { key: "updated_at", label: "Updated At", type: "datetime" },
    ],

    address: [
      {
        key: "country",
        label: "Country",
        type: "select",
        isSearchable: true,
        props: { isSearchable: true },
        placeholder: "Select Country...",
        options: countryOptions, 
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "country", (nextState, actualString) => {
          const cData = COUNTRY_TIMEZONE.find(country => country.countryName === actualString);
          
          nextState.state = ""; 
          nextState.city = "";  
          
          if (cData) {
            nextState.country_code = { value: cData.phoneCode, label: cData.phoneCode };
            nextState.currency = { value: cData.currency.code, label: `${cData.currency.code} - ${cData.currency.name}` };
            nextState.currency_symbol = { value: cData.currency.symbol, label: `${cData.currency.symbol} - ${cData.currency.name}` };
            if (cData.timezones && cData.timezones[0]) {
               nextState.time_zone = { value: cData.timezones[0].zoneName, label: `${cData.timezones[0].zoneName} (${cData.timezones[0].gmtOffset})` };
            }
          }
          return nextState;
        })
      },
      {
        key: "state",
        label: "State",
        type: "select",
        isSearchable: true,
        props: { isSearchable: true },
        placeholder: "Select State...",
        options: (currentData) => {
          const cName = safeString(currentData?.country);
          if (!cName) return [];
          
          // 🌟 Return CACHED array if it exists so search doesn't break
          if (stateOptionsCache[cName]) return stateOptionsCache[cName];

          const c = Country.getAllCountries().find(x => x.name === cName);
          if (!c) return [];
          
          const result = State.getStatesOfCountry(c.isoCode).map(s => ({
            value: s.name,
            label: s.name
          }));
          
          stateOptionsCache[cName] = result; // Save to cache
          return result;
        },
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "state", (nextState) => {
          nextState.city = ""; 
          return nextState;
        })
      },
      {
        key: "city",
        label: "City",
        type: "select",
        isSearchable: true,
        props: { isSearchable: true },
        placeholder: "Select City...",
        options: (currentData) => {
          const cName = safeString(currentData?.country);
          const sName = safeString(currentData?.state);
          if (!cName || !sName) return [];
          
          const cacheKey = `${cName}-${sName}`;
          // 🌟 Return CACHED array to protect search focus
          if (cityOptionsCache[cacheKey]) return cityOptionsCache[cacheKey];
          
          const c = Country.getAllCountries().find(x => x.name === cName);
          if (!c) return [];
          const s = State.getStatesOfCountry(c.isoCode).find(x => x.name === sName);
          if (!s) return [];
          
          const result = City.getCitiesOfState(c.isoCode, s.isoCode).map(city => ({
            value: city.name,
            label: city.name
          }));
          
          cityOptionsCache[cacheKey] = result; // Save to cache
          return result;
        },
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "city")
      },
      {
        key: "currency",
        label: "Currency",
        type: "select",
        isSearchable: true, 
        props: { isSearchable: true },
        options: currencyCodeOptions, 
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "currency")
      },
      {
        key: "currency_symbol",
        label: "Currency Symbol",
        type: "select",
        isSearchable: true, 
        props: { isSearchable: true },
        options: currencySymbolOptions, 
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "currency_symbol")
      },
      {
        key: "time_zone",
        label: "Time Zone",
        type: "select",
        isSearchable: true, 
        props: { isSearchable: true },
        options: (currentData) => {
          const cName = safeString(currentData?.country);
          if (!cName) return [];
          const cData = COUNTRY_TIMEZONE.find(c => c.countryName === cName);
          if (cData && cData.timezones) {
            return cData.timezones.map(tz => ({
              value: tz.zoneName,
              label: `${tz.zoneName} (${tz.gmtOffset})`
            }));
          }
          return [];
        },
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "time_zone")
      },
      {
        key: "country_code",
        label: "Country Phone Code",
        type: "select",
        isSearchable: true, 
        props: { isSearchable: true },
        options: phoneCodeOptions, 
        onChange: (a, b, c, d) => handleSearchableChange(a, b, c, d, "country_code")
      },
      { key: "street", label: "Street", type: "text" },
      { key: "pincode", label: "Pincode", type: "text" },
    ],

    contact: [
      { key: "systememail", label: "System Email", type: "email" },
      { key: "adminemail", label: "Admin Email", type: "email" },
    ],

    settings: [
      { key: "userlicenses", label: "User Licenses", type: "number" },
      { key: "isactive", label: "Active Status", type: "boolean" },
      { key: "is_external", label: "External Company", type: "boolean" },
      { key: "has_wallet", label: "Has Wallet", type: "boolean" },
      { key: "platform_name", label: "Platform Name", type: "text" },
    ]
  }), []);

  return (
    <ModuleDetail
      moduleName="company"
      moduleApi="company"
      moduleLabel="Company"
      fields={fields}
      permissions={permissions || {}}
      relatedModules={[
        {
          label: "Users",
          api: "user",
          filterKey: "company_id",
          columns: ["name", "email", "role"]
        },
        {
          label: "Departments",
          api: "department",
          filterKey: "company_id",
          columns: ["name", "code", "head"]
        }
      ]}
    />
  );
};

export default CompanyDetail;